import { ReactElement, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle, FormControl, IconButton, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { IAcquisitionVariableFullData } from "../../../../../types";
import { IGaugeVariable, IGaugeSegment } from "../../../types";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { cloneObject } from "../../../../../system/utils";
import StyledTableRow from "../../../../../components/styledTableRow";
import { getPalette, getPaletteColor } from "../../../../../configuration/configuration";
import { Add, Delete } from "@mui/icons-material";
import ColorPicker from "../../../../../components/colorPicker";


interface IProps {
  variable: IGaugeVariable;
  metaData: IAcquisitionVariableFullData;
  onAccept: (variable: IGaugeVariable) => void;
  onCancel: () => void;
  machineVariables: IAcquisitionVariableFullData[];
}

interface ISegmentComponentProps {
  segment: IGaugeSegment;
  machineVariables: IAcquisitionVariableFullData[];
  onUpdateSegment: (segment: IGaugeSegment) => void;
  index: number;
  onRemoveSegment: (index: number) => void;
}

const SegmentComponent = ({ segment, machineVariables, onUpdateSegment, index, onRemoveSegment }: ISegmentComponentProps): ReactElement => {
  const [ selectedColor, setSelectedColor ] = useState<string>(segment.color ?? getPaletteColor());
  const startTag = segment.start.type === "TAG" ? machineVariables.find(x => x.code === segment.start.value) ?? null : null;
  const endTag = segment.end.type === "TAG" ? machineVariables.find(x => x.code === segment.end.value) ?? null : null;

  const onChange = (field: string, value: any) => {
    if (field === "start.type") {
      segment.start.type = value;
    } else if (field === "start.value") {
      segment.start.value = value;
    } else if (field === "end.type") {
      segment.end.type = value;
    } else if (field === "end.value") {
      segment.end.value = value;
    } else {
      segment.color = `#${value.hex}`;
      setSelectedColor(value);
    }

    onUpdateSegment(segment);
  };


  return (
    <StyledTableRow hover={true} role="checkbox" tabIndex={-1} >
      <TableCell  style={{ maxWidth: 24, width: 24, padding: 4, textAlign: "right" }}>
        {index + 1}
      </TableCell>

      <TableCell>
        <Select
          sx={{ width: 100 }}
          variant="standard"
          value={segment.start.type}
          displayEmpty={true}
          onChange={(event: SelectChangeEvent) => onChange("start.type", event.target.value as string)}>
          <MenuItem value={"TAG"}>TAG</MenuItem>
          <MenuItem value={"VALUE"}>VALUE</MenuItem>
        </Select>
      </TableCell>

      <TableCell>
        {segment.start.type === "TAG" &&
          <Autocomplete
            sx={{ width: 200 }}
            options={machineVariables}
            getOptionLabel={(option: IAcquisitionVariableFullData) => option.name}
            value={startTag}
            openOnFocus={true}
            onChange={(_event: any, newValue: IAcquisitionVariableFullData | null) => {
              onChange("start.value", newValue?.code);
            }}
            renderInput={(params) =>
              <TextField {...params} variant="standard" color={"primary"}/>
            }
          />
        }

        {segment.start.type === "VALUE" &&
          <TextField
            sx={{ width: 200 }}
            variant="standard"
            size="small"
            value={(segment.start.value ?? 0) as number}
            type="number"
            onChange={(event) => onChange("start.value", parseFloat(event.target.value as string))}
          />
        }
      </TableCell>

      <TableCell>
        <Select
          sx={{ width: 100 }}
          variant="standard"
          value={segment.end.type}
          displayEmpty={true}
          onChange={(event: SelectChangeEvent) => onChange("end.type", event.target.value as string)}>
          <MenuItem value={"TAG"}>TAG</MenuItem>
          <MenuItem value={"VALUE"}>VALUE</MenuItem>
        </Select>
      </TableCell>

      <TableCell>
        {segment.start.type === "TAG" &&
          <Autocomplete
            sx={{ width: 200 }}
            options={machineVariables}
            getOptionLabel={(option: IAcquisitionVariableFullData) => option.name}
            value={endTag}
            openOnFocus={true}
            onChange={(_event: any, newValue: IAcquisitionVariableFullData | null) => {
              onChange("end.value", newValue?.code);
            }}
            renderInput={(params) =>
              <TextField {...params} variant="standard" color={"primary"}/>
            }
          />
        }

        {segment.end.type === "VALUE" &&
          <TextField
            sx={{ width: 200 }}
            variant="standard"
            size="small"
            value={(segment.end.value ?? 0) as number}
            type="number"
            onChange={(event) => onChange("end.value", parseFloat(event.target.value as string))}
          />
        }
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <ColorPicker color={selectedColor} onChange={color => onChange("color", color)} disableAlpha={true}/>
        </FormControl>
      </TableCell>

      <TableCell>
        <IconButton style={{ padding: 0, margin: 0 }} onClick={() => onRemoveSegment(index)}>
          <Tooltip title={"Remove variable"}>
            <Delete style={{ color: getPalette().statusColor.error.background }}/>
          </Tooltip>
        </IconButton>
      </TableCell>
    </StyledTableRow>
  );
};

const VariableConfigDialog = ({ variable, onAccept, machineVariables, onCancel }: IProps): ReactElement => {
  const [ data, setData ] = useState(cloneObject(variable));

  const onInternalAccept = () => {
    onAccept(data);
  };

  const onUpdateSegment = () => {
    setData({ ...data });
  };

  const addSegment = () => {
    const newData = { ...data };
    newData.segments.push({
      start: {
        type: "VALUE",
        value: 0
      },
      end: {
        type: "VALUE",
        value: 0
      },
      color: getPalette().statusColor.good.background
    });

    setData(newData);
  };

  const onRemoveSegment = (index: number) => {
    const newData = { ...data };
    variable.segments.splice(index, 1);

    setData(newData);
  };

  const onChange = (field: keyof IGaugeVariable, value: any) => {
    if (field === "minimum") {
      data[field] = value;
    } else if (field === "maximum") {
      data[field] = value;
    }

    setData({ ...data });
  };

  return (
    <Dialog onClose={onCancel} aria-labelledby="customized-dialog-title" open={true} maxWidth="md" keepMounted={true}>
      <DialogTitle id="customized-dialog-title">
        Variable Config
      </DialogTitle>
      <DialogContent dividers={true} >

        <div className="display-flex-row p-1" >
          <TextField
            className="m-1"
            label="Minimum"
            sx={{ width: 200 }}
            variant="standard"
            size="small"
            value={(data.minimum ?? 0) as number}
            type="number"
            onChange={(event) => onChange("minimum", parseFloat(event.target.value as string))}
          />

          <TextField
            className="m-1"
            label="Maximum"
            sx={{ width: 200 }}
            variant="standard"
            size="small"
            value={(data.maximum ?? 100) as number}
            type="number"
            onChange={(event) => onChange("maximum", parseFloat(event.target.value as string))}
          />
        </div>

        <Table stickyHeader={true} size={"small"} >
          <TableHead>
            <TableRow>
              <TableCell className="table-header"/>
              <TableCell className="table-header" colSpan={2} align="center">Start</TableCell>
              <TableCell className="table-header" colSpan={2} align="center">End</TableCell>
              <TableCell className="table-header"/>
              <TableCell className="table-header"/>
            </TableRow>
            <TableRow>
              <TableCell className="table-header" style={{ maxWidth: 24, width: 24, padding: 4 }}>
                <IconButton style={{ padding: 0, margin: 0, maxWidth: 21 }} onClick={addSegment} disabled={data.segments.length > 4}>
                  <Tooltip title={"Add variable"}>
                    <Add style={{ color: getPalette().statusColor.info.background }}/>
                  </Tooltip>
                </IconButton>
              </TableCell>
              <TableCell className="table-header" style={{ minWidth: 80 }}>Type</TableCell>
              <TableCell className="table-header" style={{ minWidth: 100 }}>Value</TableCell>
              <TableCell className="table-header" style={{ minWidth: 80 }}>Type</TableCell>
              <TableCell className="table-header" style={{ minWidth: 100 }}>Value</TableCell>
              <TableCell className="table-header" style={{ minWidth: 50, width: 50 }}>Color</TableCell>
              <TableCell className="table-header" />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.segments.map((s, index) => {

              return <SegmentComponent key={`segment-${index}`} index={index} segment={s} machineVariables={machineVariables} onUpdateSegment={onUpdateSegment} onRemoveSegment={onRemoveSegment} />;
            })}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onInternalAccept} variant="contained" color="primary" startIcon={<CheckIcon />}>
          Accept
        </Button>
        <Button onClick={onCancel} variant="contained" color="secondary" startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VariableConfigDialog;
